import React, { useState } from "react";
import { FileCheck2, ScrollText } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../ui/card";
import { SafQueue } from "./SafQueue";
import { SafCaseDialog } from "./SafCaseDialog";
import { SafEscalationSettingsCard } from "./SafEscalationSettingsCard";

/**
 * Phase 210 — Super Admin → Tenant Config → "Self-Assessments" tab for the selected municipality.
 * Queue (incl. Auto-escalated filter) → open a case → approve / send back / reject like the Municipal Admin, or decide a case
 * directly (mandatory reason) while it is still with the Field Collector. Also hosts the auto-escalation time limits.
 */
export function SuperAdminSafPanel({ code, testIdPrefix = "sa-saf" }) {
  const [open, setOpen] = useState(null);
  const [reload, setReload] = useState(0);
  const bump = () => setReload((k) => k + 1);

  return (
    <div className="space-y-4" data-testid={`${testIdPrefix}-panel`}>
      <Card className="shadow-sm">
        <CardHeader className="pb-2">
          <CardTitle className="text-base font-heading flex items-center gap-2"><FileCheck2 className="h-4 w-4" /> Citizen self-assessments — {code}</CardTitle>
          <CardDescription className="text-xs">
            Every open and closed self-assessment of this municipality. Open a case to approve (generates the Property PIN), send back or reject exactly as the Municipal Admin can —
            or decide a case directly with a mandatory reason while it is still with the Field Collector. Cases that crossed a time limit are tagged <b>Auto-escalated</b>.
          </CardDescription>
        </CardHeader>
        <CardContent className="p-4 pt-2">
          <SafQueue key={code} code={code} onOpen={setOpen} reloadKey={reload} testIdPrefix={testIdPrefix} pendingLabel="Pending final approval" limit={10} showEscalated />
        </CardContent>
      </Card>

      <SafEscalationSettingsCard key={`esc-${code}`} code={code} testIdPrefix={`${testIdPrefix}-escalation`} onSwept={bump} />

      <p className="text-xs text-muted-foreground flex items-start gap-1.5" data-testid={`${testIdPrefix}-log-hint`}>
        <ScrollText className="h-3.5 w-3.5 mt-0.5 shrink-0" />
        Every assignment, escalation and direct decision is recorded in the municipality's SAF Approval &amp; Modification Log (Admin → Reports → SAF Approval Log).
      </p>

      <SafCaseDialog code={code} saf={open} role="super_admin" open={!!open} onClose={() => setOpen(null)}
        onChanged={() => { bump(); }} testIdPrefix={`${testIdPrefix}-case`} />
    </div>
  );
}
